import {ERequestType} from 'shared/types/requests';
import {performRequest} from 'shared/utils';
import {isProduction} from 'shared/utils/is-production';
import {EAuthRoutes} from 'store/slices/auth-slice/auth-slice-routes';
import {IAuthResponse} from 'store/slices/auth-slice/auth-slice-types';

export interface IAuthRequestArgs {
    username: string;
    password: string;
    twoFactorAuthCode?: string;
    isPortal2faAuth?: boolean;
}
export const authRequest = ({
    username,
    password,
    twoFactorAuthCode,
}: IAuthRequestArgs): Promise<IAuthResponse> => {
    const params = new URLSearchParams();
    params.append('username', username);
    params.append('password', password);
    if (twoFactorAuthCode) {
        params.append('code', twoFactorAuthCode);
    }

    const baseUrl = isProduction()
        ? window.location.origin
        : process.env.REACT_APP_BASE_URL;
    const url = `${baseUrl}/nalmon/login`;

    return (
        fetch(url, {
            method: ERequestType.POST,
            body: params,
            headers: {
                'x-react-req-id': '1', // react requests mark for backend
            },
        }).then(r => r.json())
    );
};

export interface IChangePasswordRequestArgs {
    currentPassword: string;
    newPassword: string;
    confirmNewPassword: string;
}
export const changePasswordRequest = ({
    currentPassword,
    newPassword,
    confirmNewPassword,
}: IChangePasswordRequestArgs) => {
    const params = new URLSearchParams();
    params.append('currentPassword', currentPassword);
    params.append('newPassword', newPassword);
    params.append('confirmNewPassword', confirmNewPassword);

    return (
        performRequest({
            url: EAuthRoutes.CHANGE_PASSWORD,
            method: ERequestType.POST,
            data: params,
            headers: {
                'Access-Control-Allow-Origin': window.location.origin,
                'Access-Control-Allow-Credentials': true,
            },
            withCredentials: true,
        })
    );
};

export const fetchUserInfo = () => (
    performRequest({
        url: EAuthRoutes.USER_INFO,
        method: ERequestType.GET,
    })
);

export const logoutRequest = () => (
    performRequest({
        url: EAuthRoutes.LOGOUT,
        method: ERequestType.POST,
    })
);

export const fetchTwoFAConfig = () => (
    performRequest({
        url: EAuthRoutes.GET_TWO_FA_CONFIG,
        method: ERequestType.GET,
    })
);

export const fetchMicroserviceToken = () => (
    performRequest({
        url: EAuthRoutes.MICROSERVICE_TOKEN,
        method: ERequestType.GET,
    })
);

export type TFetchTwoFACodeArgs = Pick<IAuthRequestArgs, 'username'> & { password?: IAuthRequestArgs['password'] };

export const fetchTwoFACode = ({username, password}: TFetchTwoFACodeArgs) => {
    const params = new URLSearchParams();
    params.append('username', username);

    if (password) {
        params.append('password', password);
    }

    return (
        performRequest({
            url: EAuthRoutes.GET_TWO_FA_CODE,
            method: ERequestType.POST,
            data: params,
        })
    );
};

export const fetchChangePasswordSettings = () => (
    performRequest({
        url: EAuthRoutes.CHANGE_PASSWORD_SETTINGS,
        method: ERequestType.GET,
    })
);

export const fetch2faCodeValidity = () => (
    performRequest({
        url: EAuthRoutes.GET_TWO_FA_CODE_VALIDITY,
        method: ERequestType.GET,
    })
);

export type TVerify2faArgs = Pick<IAuthRequestArgs, 'twoFactorAuthCode'>

export const verify2fa = ({twoFactorAuthCode}: TVerify2faArgs) => (
    performRequest({
        url: EAuthRoutes.VALIDATE_TWO_FA,
        method: ERequestType.POST,
        data: {code: twoFactorAuthCode}
    })
);

export const fetchAuthProfile = () => (
    performRequest({
        url: EAuthRoutes.PROFILE,
        method: ERequestType.GET,
    })
);

export const fetchAuthStatus = () => (
    performRequest({
        url: EAuthRoutes.AUTHENTICATION,
        method: ERequestType.GET,
    })
);

export const getSessionId = () => performRequest({
    url: EAuthRoutes.GET_SESSION_ID,
    method: ERequestType.GET,
});
